import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItem, ListItemIcon, ListItemText,
  Alert, LinearProgress, Typography, Chip, Divider, Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReplayIcon from '@mui/icons-material/Replay';
import { ApiRequest } from 'GlobalFunctions';
import { useSnackbar } from 'notistack';

interface BatchOrder {
  batch_order_id: number;
  order_id: number;
  company_order_id: string;
  status: string;
  error_message: string | null;
  retry_count: number;
  client_name: string;
}

interface BatchGroup {
  batch_id: number;
  warehouse_name: string;
  created_at: string;
  created_by_name: string;
  total_orders: number;
  success_count: number;
  failed_count: number;
  orders: BatchOrder[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onRetryDone: () => void;
}

export default function BatchDetailDialog({ open, onClose, onRetryDone }: Props) {
  const [batches, setBatches] = useState<BatchGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryProgress, setRetryProgress] = useState({ current: 0, total: 0 });
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) loadAll();
  }, [open]);

  const loadAll = async () => {
    setLoading(true);
    // First get list of partial batches
    ApiRequest({
      url: '/orders/batches',
      method: 'get',
      headers: { ltkn: localStorage.getItem('ltkn') },
      query: { status: 'partial' },
      setResponse: async (response: any) => {
        if (response.status === 'success' && response.batches?.length) {
          // Load detail for each batch
          const details: BatchGroup[] = [];
          let loaded = 0;
          for (const b of response.batches) {
            ApiRequest({
              url: `/orders/batch/${b.batch_id}`,
              method: 'get',
              headers: { ltkn: localStorage.getItem('ltkn') },
              setResponse: (r: any) => {
                loaded++;
                if (r.status === 'success') {
                  details.push({
                    batch_id: r.batch.batch_id,
                    warehouse_name: r.batch.warehouse_name,
                    created_at: r.batch.created_at,
                    created_by_name: r.batch.created_by_name,
                    total_orders: r.batch.total_orders,
                    success_count: r.batch.success_count,
                    failed_count: r.batch.failed_count,
                    orders: r.orders || [],
                  });
                }
                if (loaded === response.batches.length) {
                  details.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                  setBatches(details);
                  setLoading(false);
                }
              },
            });
          }
        } else {
          setBatches([]);
          setLoading(false);
        }
      },
    });
  };

  const handleRetryAll = () => {
    const batchIds = batches.filter(b => b.failed_count > 0).map(b => b.batch_id);
    if (!batchIds.length) return;

    setRetrying(true);
    setRetryProgress({ current: 0, total: batchIds.length });

    let completed = 0;
    let totalSuccess = 0;
    let totalFailed = 0;

    const retryNext = (index: number) => {
      if (index >= batchIds.length) {
        setRetrying(false);
        if (totalFailed === 0) {
          enqueueSnackbar(`Todos los pedidos fueron emitidos exitosamente`, { variant: 'success' });
        } else {
          enqueueSnackbar(`${totalSuccess} exitoso${totalSuccess > 1 ? 's' : ''}, ${totalFailed} aún con error`, { variant: 'warning' });
        }
        loadAll();
        onRetryDone();
        return;
      }

      ApiRequest({
        url: '/orders/batch-retry',
        method: 'post',
        headers: { ltkn: localStorage.getItem('ltkn') },
        body: { batch_id: batchIds[index] },
        setResponse: (r: any) => {
          completed++;
          setRetryProgress({ current: completed, total: batchIds.length });
          if (r.status === 'success') {
            const s = r.results?.filter((x: any) => x.success).length || 0;
            const f = r.results?.filter((x: any) => !x.success).length || 0;
            totalSuccess += s;
            totalFailed += f;
          }
          retryNext(index + 1);
        },
      });
    };

    retryNext(0);
  };

  const handleClose = () => {
    setBatches([]);
    onClose();
  };

  const totalFailed = batches.reduce((s, b) => b.orders.filter(o => o.status === 'failed').length + s, 0);
  const totalSuccess = batches.reduce((s, b) => b.orders.filter(o => o.status === 'success').length + s, 0);
  const totalOrders = batches.reduce((s, b) => b.total_orders + s, 0);

  return (
    <Dialog open={open} onClose={retrying ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Facturación en bloque pendiente</DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {!loading && batches.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <Typography variant="body2" color="text.secondary">
                {totalOrders} pedidos · {totalSuccess} exitosos · {totalFailed} con error
              </Typography>
            </div>

            {retrying && (
              <>
                <LinearProgress
                  variant={retryProgress.total > 1 ? 'determinate' : 'indeterminate'}
                  value={retryProgress.total > 1 ? (retryProgress.current / retryProgress.total) * 100 : undefined}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mb: 2 }}>
                  Reintentando bloque {retryProgress.current + 1} de {retryProgress.total}...
                </Typography>
              </>
            )}

            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              {batches.map((batch, idx) => (
                <div key={batch.batch_id} style={{ marginBottom: idx < batches.length - 1 ? 16 : 0 }}>
                  <Typography variant="caption" style={{ color: '#999', fontWeight: 500 }}>
                    Bloque #{batch.batch_id} · {batch.warehouse_name} · {new Date(batch.created_at).toLocaleString('es-CO', {
                      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                    })}
                  </Typography>
                  <List dense disablePadding sx={{ mt: 0.5 }}>
                    {batch.orders.map((order) => (
                      <ListItem key={order.batch_order_id} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          {order.status === 'success' ? (
                            <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                          ) : order.status === 'failed' ? (
                            <ErrorIcon color="error" sx={{ fontSize: 18 }} />
                          ) : (
                            <HourglassEmptyIcon color="disabled" sx={{ fontSize: 18 }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" style={{ fontSize: 13 }}>
                              <strong>#{order.company_order_id}</strong>
                              {order.client_name && <span style={{ color: '#666' }}> — {order.client_name}</span>}
                              {order.retry_count > 1 && (
                                <Chip label={`×${order.retry_count}`} size="small" sx={{ ml: 0.5, height: 16, fontSize: 10 }} />
                              )}
                            </Typography>
                          }
                          secondary={
                            order.status === 'failed' && order.error_message ? (
                              <Tooltip title={order.error_message} arrow placement="bottom-start">
                                <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 380, display: 'block', fontSize: 11 }}>
                                  {order.error_message}
                                </Typography>
                              </Tooltip>
                            ) : undefined
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                  {idx < batches.length - 1 && <Divider sx={{ mt: 1 }} />}
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && batches.length === 0 && (
          <Alert severity="success">No hay pedidos pendientes por reintentar.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        {totalFailed > 0 && !retrying && (
          <Button
            onClick={handleRetryAll}
            color="warning"
            variant="contained"
            startIcon={<ReplayIcon />}
            style={{ textTransform: 'none' }}
          >
            Reintentar todos ({totalFailed})
          </Button>
        )}
        <Button onClick={handleClose} disabled={retrying}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
